import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthGate } from './auth/AuthGate.js';
import { Planning } from './Planning.js';
import { Pointages } from './Pointages.js';
import { Menage } from './Menage.js';
import { FrontDesk } from './FrontDesk.js';
import { FrontDeskProvider } from './FrontDeskProvider.js';
import { StaffProvider } from './StaffProvider.js';
import { AppCapabilityProvider } from './AppCapabilityProvider.js';
import { AppBrandingProvider } from './AppBrandingProvider.js';
import { AppBrand } from '@lemontaigu/platform-ui';
import { disablePush, enablePush, getPushState, type PushState } from './push.js';
import { hardReload, registerServiceWorker, type SwUpdate } from '@lemontaigu/reception/pwa/swUpdate.js';
import { InstallHint } from '@lemontaigu/reception/pwa/InstallHint.js';
import { supabase } from './supabase.js';
import '@lemontaigu/reception/styles/team.css';
import '@lemontaigu/reception/styles/fr-date.css';
import './styles/index.css';

// staff.* = the individual employee's account (site reassessment 2026-07-14,
// APPS-REORG R2 executed): chat, presence board, own digests and own relevé.
// Operator surfaces (roster/RUP, corrections, AI curation) live on
// bos.lemontaigu.com only. « Planning » (2026-07-19; menu label 2026-07-22)
// adds the week/month Présence matrix (@lemontaigu/reception's
// PresenceMatrix, read-only) right after the day view's activity Gantt.
// « Sur place » (who's clocked in right now) is dropped (2026-07-22) — the
// Day Gantt's Réalisé chart (reception/bos) and the pointeuse's Aujourd'hui
// list already cover it. Messages and Synthèses are parked from the menu
// (2026-07-23 — WhatsApp habit persists, pending staff training); the
// components stay in the codebase for when the menu items return.
// « Arrivées · Départs » (2026-09-30): the day's rooms still to arrive and the
// departures still to collect — rooms, nights, amount, no names, no history.
type View = 'presence' | 'pointages' | 'menage' | 'arrivees';
const VIEWS: View[] = ['presence', 'pointages', 'menage', 'arrivees'];
const viewFromHash = (): View => {
  const hash = window.location.hash.replace('#', '');
  if (hash === 'planning') return 'presence'; // pre-merge bookmark → the unified « Présence » menu
  return (VIEWS as string[]).includes(hash) ? (hash as View) : 'presence';
};

/** Web Push toggle (M2). Hidden until VAPID_PUBLIC_KEY is configured. */
function PushToggle() {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getPushState().then(setState);
  }, []);

  if (state === 'unsupported' || state === 'unconfigured') return null;
  if (state === 'denied') {
    return (
      <span className="app-push is-denied" title="Notifications bloquées dans le navigateur" aria-label="Notifications bloquées dans le navigateur">
        🔕
      </span>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      setState(state === 'on' ? await disablePush(supabase) : await enablePush(supabase));
    } catch {
      setState(await getPushState());
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`app-push ${state === 'on' ? 'is-on' : ''}`}
      disabled={busy}
      title={state === 'on' ? 'Alertes activées' : 'Activer les alertes'}
      aria-label={state === 'on' ? 'Alertes activées' : 'Activer les alertes'}
      onClick={() => void toggle()}
    >
      {state === 'on' ? '🔔' : '🔕'}
    </button>
  );
}

function App() {
  const [view, setView] = useState<View>(viewFromHash);
  const [swUpdate, setSwUpdate] = useState<SwUpdate | null>(null);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    registerServiceWorker(setSwUpdate);
  }, []);

  const go = (v: View) => {
    window.location.hash = v;
    setView(v);
  };

  const activeView = view;

  return (
    <AuthGate>
      <AppBrandingProvider>
      {swUpdate && (
        <div className="update-banner">
          <span>Nouvelle version disponible.</span>
          <button type="button" onClick={() => swUpdate.apply()}>
            Recharger
          </button>
        </div>
      )}
      <InstallHint />
      <nav className="app-nav">
        <AppBrand app="Mon Compte" />
        <div className="app-tabs">
          <button
            type="button"
            className={activeView === 'presence' ? 'is-active' : ''}
            onClick={() => go('presence')}
          >
            Planning
          </button>
          <button
            type="button"
            className={activeView === 'pointages' ? 'is-active' : ''}
            onClick={() => go('pointages')}
          >
            Pointages
          </button>
          {/* « Ménage » — which rooms, not who is working, so its own tab with
              its own day rather than a strip under Planning. */}
          <button
            type="button"
            className={activeView === 'menage' ? 'is-active' : ''}
            onClick={() => go('menage')}
          >
            Ménage
          </button>
          <button
            type="button"
            className={activeView === 'arrivees' ? 'is-active' : ''}
            onClick={() => go('arrivees')}
          >
            Arrivées · Départs
          </button>
        </div>
        <div className="app-nav-actions">
          <button type="button" className="pwa-refresh-btn" title="Recharger l’application" aria-label="Recharger l’application" onClick={hardReload}>
            🔄
          </button>
          <PushToggle />
          <button type="button" className="app-signout" onClick={() => void supabase.auth.signOut()}>
            Se déconnecter
          </button>
        </div>
      </nav>
      <main className="app-main">
        {/* C3's read ports are bound once, here — see StaffProvider. The
            capability context wraps it: services gated at L2 (increment 3)
            will need an actor, and it must exist above the root that builds
            them. */}
        <AppCapabilityProvider>
          <StaffProvider>
            {activeView === 'presence' && <Planning />}
            {activeView === 'pointages' && <Pointages />}
            {activeView === 'menage' && <Menage />}
            {/* C4's own root: the PMS list is not a C3 store. */}
            {activeView === 'arrivees' && (
              <FrontDeskProvider>
                <FrontDesk />
              </FrontDeskProvider>
            )}
          </StaffProvider>
        </AppCapabilityProvider>
      </main>
      </AppBrandingProvider>
    </AuthGate>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
